import { ArrowLeft, Calendar, Droplets, TrendingUp } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { useState, useEffect } from "react";
import welcomeBg from "../../assets/homebg.png";
import fitnessService, { FitnessData } from "../../services/FitnessService";

const SpO2Detail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("weekly");
  const [fitnessData, setFitnessData] = useState<FitnessData | null>(
    location.state?.fitnessData || null
  );
  const [loading, setLoading] = useState(!location.state?.fitnessData);

  // Get selected date from navigation state or use today
  const selectedDate =
    location.state?.selectedDate || new Date().toISOString().split("T")[0];

  useEffect(() => {
    // Only fetch if data not passed from previous page
    if (!location.state?.fitnessData) {
      const fetchSpO2Data = async () => {
        try {
          setLoading(true);
          const result = await fitnessService.getDailyFitnessData(selectedDate);
          const isSuccess =
            typeof result.success === "boolean"
              ? result.success
              : result.success?.code === "Retrieved";
          if (result && isSuccess && result.data) {
            setFitnessData(result.data);
          }
        } catch (error) {
          // Handle error silently
        } finally {
          setLoading(false);
        }
      };

      fetchSpO2Data();
    } else {
    }
  }, [selectedDate, location.state?.fitnessData]);

  const weeklyData = [
    { day: "Mon", spo2: 98 },
    { day: "Tue", spo2: 97 },
    { day: "Wed", spo2: 98 },
    { day: "Thu", spo2: 99 },
    { day: "Fri", spo2: 98 },
    { day: "Sat", spo2: 98 },
    { day: "Sun", spo2: 98 },
  ];

  const monthlyData = [
    { week: "Week 1", spo2: 97.8 },
    { week: "Week 2", spo2: 98.2 },
    { week: "Week 3", spo2: 97.9 },
    { week: "Week 4", spo2: 98.1 },
  ];

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 0px)" }}
      >
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() =>
              navigate("/glucera/fitness", {
                state: {
                  selectedDate: selectedDate,
                  selectedDateObj: location.state?.selectedDateObj,
                },
              })
            }
            className="rounded-xl bg-card/80 hover:bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="text-center">
            <h1 className="text-xl font-bold text-foreground">SpO2</h1>
            <p className="text-xs text-muted-foreground">Blood oxygen levels</p>
          </div>
          <div className="h-10 w-10" />
        </div>

        <Card className="rounded-2xl border border-border bg-gradient-to-br from-cyan-50 to-cyan-100 p-5 shadow-lg dark:from-cyan-950 dark:to-cyan-900">
          <div className="mb-4 flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-cyan-500/20">
              <Droplets className="h-7 w-7 text-cyan-600" />
            </div>
            <div className="flex-1">
              <div className="text-3xl font-bold text-foreground">
                {loading ? (
                  <span className="text-xl">Loading...</span>
                ) : (
                  <>
                    {fitnessData?.spo2_percent || "--"}
                    <span className="text-xl">%</span>
                  </>
                )}
              </div>
              <p className="text-sm text-muted-foreground">Current SpO2</p>
            </div>
          </div>
        </Card>

        <Card className="rounded-2xl border border-border bg-amber-50/30 p-4 shadow-lg dark:bg-amber-950/30">
          <div className="flex gap-3">
            <div className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded bg-amber-500">
              <span className="text-xs font-bold text-white">!</span>
            </div>
            <div>
              <h4 className="mb-1 text-sm font-semibold text-amber-800">
                Sleep Tracking Required
              </h4>
              <p className="text-xs leading-relaxed text-amber-700">
                SpO2 is recorded only when you wear your tracker watch while
                sleeping. Make sure to wear it overnight for accurate readings.
              </p>
            </div>
          </div>
        </Card>

        <Card className="rounded-2xl border border-border bg-cyan-50/30 p-4 shadow-lg dark:bg-cyan-950/30">
          <div className="space-y-4">
            <div className="flex gap-3">
              <TrendingUp className="mt-0.5 h-5 w-5 flex-shrink-0 text-cyan-600" />
              <div>
                <h4 className="mb-2 text-lg font-bold text-foreground">
                  What is SpO2?
                </h4>
                <p className="mb-3 text-sm leading-relaxed text-muted-foreground">
                  <span className="font-semibold">
                    Blood Oxygen Saturation (SpO2)
                  </span>{" "}
                  measures the percentage of oxygen-carrying hemoglobin in your
                  blood compared to the total amount of hemoglobin.
                </p>
                <p className="mb-3 text-sm leading-relaxed text-muted-foreground">
                  <span className="font-semibold">Normal levels</span> are
                  typically between{" "}
                  <span className="font-semibold text-cyan-600">95-100%</span>.
                  Your body needs a certain level of oxygen in your blood to
                  function properly.
                </p>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  <span className="font-semibold">During sleep</span>, SpO2 can
                  naturally dip slightly. Significant or prolonged drops may
                  indicate sleep apnea or other respiratory conditions and
                  should be discussed with a healthcare provider.
                </p>
              </div>
            </div>
          </div>
        </Card>

        <Card className="rounded-2xl border border-border bg-red-50/30 p-4 shadow-lg dark:bg-red-950/30">
          <div className="space-y-3">
            <h4 className="flex items-center gap-2 text-lg font-bold text-red-800">
              <div className="h-2 w-2 rounded-full bg-red-500" />
              When to Be Concerned
            </h4>
            <div className="space-y-2">
              <div className="flex items-start gap-3">
                <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-red-500" />
                <p className="text-sm text-muted-foreground">
                  Consistently below 95% during sleep
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-red-500" />
                <p className="text-sm text-muted-foreground">
                  Frequent dips below 90%
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-red-500" />
                <p className="text-sm text-muted-foreground">
                  Symptoms like shortness of breath or fatigue
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-red-500" />
                <p className="text-sm text-muted-foreground">
                  Snoring or gasping during sleep
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* <Card className="rounded-2xl border border-gray-100 bg-white shadow-lg">
          <div className="m-4 grid w-full grid-cols-2 gap-2 rounded-xl bg-gray-100 p-1">
            <button
              onClick={() => setActiveTab("weekly")}
              className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                activeTab === "weekly"
                  ? "bg-white text-gray-900 shadow"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Weekly
            </button>
            <button
              onClick={() => setActiveTab("monthly")}
              className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                activeTab === "monthly"
                  ? "bg-white text-gray-900 shadow"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Monthly
            </button>
          </div>

          <div className="space-y-4 px-4 pb-4">
            {activeTab === "weekly" && (
              <div>
                <h3 className="mb-4 font-semibold text-gray-900">
                  This Week's SpO2
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={weeklyData}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#e5e7eb"
                        opacity={0.3}
                      />
                      <XAxis dataKey="day" stroke="#6b7280" fontSize={12} />
                      <YAxis
                        stroke="#6b7280"
                        fontSize={12}
                        domain={[95, 100]}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "2px solid hsl(var(--border))",
                          borderRadius: "12px",
                          color: "hsl(var(--foreground))",
                          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.2)",
                        }}
                        itemStyle={{ color: "hsl(var(--foreground))" }}
                        labelStyle={{ color: "hsl(var(--muted-foreground))" }}
                      />
                      <Line
                        type="monotone"
                        dataKey="spo2"
                        stroke="#06b6d4"
                        strokeWidth={2.5}
                        dot={{ fill: "#06b6d4", r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {activeTab === "monthly" && (
              <div>
                <h3 className="mb-4 font-semibold text-gray-900">
                  This Month's SpO2
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={monthlyData}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#e5e7eb"
                        opacity={0.3}
                      />
                      <XAxis dataKey="week" stroke="#6b7280" fontSize={12} />
                      <YAxis
                        stroke="#6b7280"
                        fontSize={12}
                        domain={[95, 100]}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "2px solid hsl(var(--border))",
                          borderRadius: "12px",
                          color: "hsl(var(--foreground))",
                          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.2)",
                        }}
                        itemStyle={{ color: "hsl(var(--foreground))" }}
                        labelStyle={{ color: "hsl(var(--muted-foreground))" }}
                      />
                      <Line
                        type="monotone"
                        dataKey="spo2"
                        stroke="#06b6d4"
                        strokeWidth={2.5}
                        dot={{ fill: "#06b6d4", r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        </Card> */}
      </div>
    </div>
  );
};

export default SpO2Detail;
